/**
 * What each kind of case attachment is allowed to be.
 *
 * The three uploads carry different things — clinical photographs, a
 * radiograph, and an intraoral scan — and nothing but the label used to
 * say so, which let an STL land in Photos and a JPEG land in Scans.
 *
 * `accept` filters the file dialog; `extensions` is what the picker
 * actually enforces, since a drag-and-drop bypasses `accept` entirely.
 * Keep these in step with the matching lists in CaseController::
 * allowedFileExtensions() — the server is the real gate.
 */
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];

// A radiograph comes off the machine either as a plain image or as DICOM.
const XRAY_EXTENSIONS = [...PHOTO_EXTENSIONS, 'dcm', 'dicom'];

// A scan is the STL itself, or a ZIP or RAR holding the upper and lower
// arch together, which is how most scanners export a case.
const SCAN_EXTENSIONS = ['stl', 'zip', 'rar'];

const toAccept = (extensions) => extensions.map((e) => `.${e}`).join(',');

// A brand logo and a profile picture are pictures — nothing else. These
// two slots used to declare no extension list at all, and `accept`
// alone is only a hint to the file dialog, so a dropped video was taken
// without a word of complaint.
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'svg'];

export const MEDIA_FORMATS = {
  image: {
    extensions: IMAGE_EXTENSIONS,
    accept: toAccept(IMAGE_EXTENSIONS),
    label: 'JPG, PNG, WEBP or SVG',
    previewable: true,
  },
  photo: {
    extensions: PHOTO_EXTENSIONS,
    accept: toAccept(PHOTO_EXTENSIONS),
    label: 'JPG, PNG, WEBP or HEIC',
    previewable: true,
  },
  xray: {
    extensions: XRAY_EXTENSIONS,
    accept: toAccept(XRAY_EXTENSIONS),
    label: 'JPG, PNG, WEBP, HEIC or DICOM',
    previewable: true,
  },
  scan: {
    extensions: SCAN_EXTENSIONS,
    accept: toAccept(SCAN_EXTENSIONS),
    label: 'STL, ZIP or RAR',
    // An STL is mesh data, not an image — the preview eye opened it in
    // the image viewer and showed a broken frame.
    previewable: false,
  },
};

/** Lowercase extension of a filename, without the dot. Empty if it has none. */
export const fileExtension = (name = '') => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

export const isAcceptedFile = (name, extensions) =>
  extensions.includes(fileExtension(name));

/**
 * How much a case submission may carry.
 *
 * PHP refuses a request larger than post_max_size before Laravel routes
 * it, so an oversized batch used to come back as a stack trace and the
 * screen could only say "Case not created". Checking here means the
 * reader is told which files are the problem while they can still do
 * something about it.
 *
 * Keep in step with server/public/.htaccess and .user.ini: 100M per
 * file, 256M per request there — the gap between the two absorbs the
 * multipart overhead and the form's own fields.
 */
export const MAX_SINGLE_FILE_MB = 100;

export const MAX_UPLOAD_MB = 240;

const MB = 1024 * 1024;

export const formatMb = (bytes) => `${(bytes / MB).toFixed(1)} MB`;

/**
 * Check a whole submission's worth of files.
 *
 * Returns null when everything fits, otherwise { title, description }
 * ready to hand to a toast.
 */
export const checkUploadSize = (groups) => {
  const files = Object.values(groups || {}).flat().filter(Boolean);

  const tooBig = files.filter((file) => file.size > MAX_SINGLE_FILE_MB * MB);
  if (tooBig.length > 0) {
    return {
      title: tooBig.length === 1 ? "That file is too large" : "Some files are too large",
      description: `${tooBig.map((f) => `${f.name} (${formatMb(f.size)})`).join(', ')} — the limit is ${MAX_SINGLE_FILE_MB} MB per file.`,
    };
  }

  const total = files.reduce((sum, file) => sum + (file.size || 0), 0);
  if (total > MAX_UPLOAD_MB * MB) {
    return {
      title: "These files are too large to upload at once",
      description: `The ${files.length} selected files come to ${formatMb(total)}; the limit is ${MAX_UPLOAD_MB} MB per case. Save the case with fewer files, then add the rest by editing it.`,
    };
  }

  return null;
};
